import { ZONE_PALETTE } from '../../zone-colors'
import { useMapStateStore } from '../core'

export enum DrawMode {
  Draw = 'draw',
  Erase = 'erase', //only allow if at least one feature exists
  Lasso = 'lasso', //like polygon but only allows 1 shape at a time

  Split = 'split',
  AddZone = 'zone',
  AssignZone = 'merge',

  //guidance modes
  ABLine = 'abLine',
  AHeading = 'aHeading',
  PivotFromCurve = 'pivotFromCurve',
  RadialPivot = 'radialPivot'
}

export enum DrawStyle {
  Polygon = 'polygon',
  Freehand = 'freehand',
  Circle = 'circle',
  Rectangle = 'rectangle',
  Line = 'line'
}

/**
 * What kind of scene the draw tool is currently rendering.
 *
 * - `Boundary`: drawing/editing the field boundary with no zone color.
 * - `ZoneEditing`: drawing/editing with zone palette applied to each feature.
 * - `Lines`: drawing/editing guidance line primitives inside the zone palette.
 *   Zone polygons stay visible as context; guidance primitives only render in
 *   this scene so they don't clutter other tabs.
 * - `EdgeSelection`: drawing disabled; view-only edges (from headlands) are
 *   colored and clickable. Drawn shapes remain visible but uncolored.
 * - `HoleSelection`: drawing disabled; view-only holes are colored and
 *   clickable. Other view-only shapes (e.g. edges) remain uncolored.
 * - `CornerSelection`: drawing disabled; view-only corner points are
 *   rendered with their per-corner color. Other view-only shapes stay
 *   uncolored.
 */
export enum DrawScene {
  Boundary = 'boundary',
  ZoneEditing = 'zoneEditing',
  Lines = 'lines',
  EdgeSelection = 'edgeSelection',
  HoleSelection = 'holeSelection',
  CornerSelection = 'cornerSelection',
  AllOverrides = 'allOverrides'
}

export const useDrawMode = () => {
  return useMapStateStore((state) => state.drawTool.interaction)
}

export const useDrawShape = () => {
  return useMapStateStore((state) => state.drawTool.shape)
}

export const useDrawScene = () => {
  return useMapStateStore((state) => state.drawTool.scene)
}

export const getDrawScene = (): DrawScene => {
  return useMapStateStore.getState().drawTool.scene
}

export const setDrawScene = (scene: DrawScene) => {
  useMapStateStore.setState((state) => ({
    drawTool: { ...state.drawTool, scene }
  }))
}

export const useCurrentZones = () => {
  return useMapStateStore((state) => state.drawTool.zoneEditing)
}

export const useSelectedId = () => {
  return useMapStateStore((state) => state.drawTool.selectedId)
}

export const useActiveZone = () => {
  return useMapStateStore((state) => {
    const id = state.drawTool.selectedId
    return id === null ? undefined : state.drawTool.zoneEditing?.zones.get(id)
  })
}

/**
 * Fires only when draw-tool state that affects rendering (scene, selected id,
 * or zone palette) changes. Deliberately excludes `enabled`, `interaction`,
 * and `shape` — those only drive the draw interaction itself, not feature
 * styling, so re-running per-feature styles on those changes would be wasted
 * work that also cascades through any `source.changed()` listeners (e.g. the
 * `onChange` handler used to re-run helm queries).
 */
export const subscribeToDrawToolStyleChanges = (callback: () => void) => {
  return useMapStateStore.subscribe(
    (state) => ({
      scene: state.drawTool.scene,
      selectedId: state.drawTool.selectedId,
      zones: state.drawTool.zoneEditing?.zones
    }),
    callback,
    {
      equalityFn: (a, b) =>
        a.scene === b.scene && a.selectedId === b.selectedId && a.zones === b.zones
    }
  )
}

export const setSelectedId = (id: string | null) => {
  useMapStateStore.setState((state) => ({
    drawTool: { ...state.drawTool, selectedId: id }
  }))
}

export const getSelectedId = (): string | null => {
  return useMapStateStore.getState().drawTool.selectedId
}

export const enableDrawTool = (interaction: DrawMode, shape: DrawStyle) => {
  useMapStateStore.setState((state) => ({
    drawTool: { ...state.drawTool, enabled: true, interaction, shape }
  }))
}

export const disableDrawTool = () => {
  useMapStateStore.setState((state) => ({
    drawTool: { ...state.drawTool, enabled: false, activeGuidanceLine: undefined }
  }))
}

export const isDrawToolEnabled = () => {
  return useMapStateStore.getState().drawTool.enabled
}

let zoneColorIndex = 0

const getNextColorInCycle = () => {
  const color = ZONE_PALETTE[zoneColorIndex % ZONE_PALETTE.length]
  zoneColorIndex++
  return color
}

export const createZone = (zone: { id?: string; name: string }) => {
  const newZone = {
    id: zone.id ?? crypto.randomUUID(),
    name: zone.name,
    color: getNextColorInCycle()
  }
  useMapStateStore.setState((state) => {
    const zoneEditing = state.drawTool.zoneEditing
    return {
      drawTool: {
        ...state.drawTool,
        scene: DrawScene.ZoneEditing,
        selectedId: newZone.id,
        zoneEditing: {
          ...zoneEditing,
          zones: new Map(zoneEditing?.zones).set(newZone.id, newZone)
        }
      }
    }
  })
  return newZone
}

export const getZoneById = (id: string) => {
  const state = useMapStateStore.getState()
  const zones = state.drawTool.zoneEditing?.zones
  return zones?.get(id) ?? null
}

export const getCurrentActiveZoneId = (): string | undefined => {
  const state = useMapStateStore.getState()
  const id = state.drawTool.selectedId
  if (id === null) return undefined
  return state.drawTool.zoneEditing?.zones.has(id) === true ? id : undefined
}

export const isZoneEditingActive = (): boolean => {
  return useMapStateStore.getState().drawTool.zoneEditing !== undefined
}

export const setActiveZone = (zoneId?: string) => {
  useMapStateStore.setState((state) => {
    const zoneEditing = state.drawTool.zoneEditing
    if (!zoneEditing) {
      console.warn('Attempting to set active zone while not in zone editing mode')
      return state
    }
    if (zoneId === undefined) {
      return { drawTool: { ...state.drawTool, selectedId: null } }
    }
    if (!zoneEditing.zones.has(zoneId)) {
      console.warn(`Attempting to set active zone to non-existent zone with id ${zoneId}`)
      return state
    }
    return { drawTool: { ...state.drawTool, selectedId: zoneId } }
  })
}

export const resetZoneEditing = () => {
  zoneColorIndex = 0
  useMapStateStore.setState((state) => ({
    drawTool: {
      ...state.drawTool,
      scene: DrawScene.Boundary,
      selectedId: null,
      zoneEditing: undefined,
      activeGuidanceLine: undefined
    }
  }))
}

export const restoreZones = (zones: { id: string; name: string; color: string }[]) => {
  if (zones.length === 0) return
  zoneColorIndex = zones.length
  useMapStateStore.setState((state) => ({
    drawTool: {
      ...state.drawTool,
      scene: DrawScene.ZoneEditing,
      selectedId: zones[0].id,
      zoneEditing: {
        zones: new Map(zones.map((z) => [z.id, z]))
      }
    }
  }))
}

export const setActiveGuidanceLine = (
  featureId: string,
  pointA: [number, number],
  pointB: [number, number],
  radiusFt?: number
) => {
  useMapStateStore.setState((state) => ({
    drawTool: {
      ...state.drawTool,
      activeGuidanceLine: { id: featureId, pointA, pointB, radiusFt }
    }
  }))
}

export const clearActiveGuidanceLine = () => {
  useMapStateStore.setState((state) => ({
    drawTool: { ...state.drawTool, activeGuidanceLine: undefined }
  }))
}

export const useActiveGuidanceLine = () => {
  return useMapStateStore((state) => state.drawTool.activeGuidanceLine)
}

export const getCurrentActiveGuidanceLine = () => {
  return useMapStateStore.getState().drawTool.activeGuidanceLine
}

export const deleteZone = (zoneId: string) => {
  useMapStateStore.setState((state) => {
    const zoneEditing = state.drawTool.zoneEditing
    if (!zoneEditing) {
      console.warn('Attempting to delete a zone while not in zone editing mode')
      return state
    }
    const newZones = new Map(zoneEditing.zones)
    newZones.delete(zoneId)
    const nextSelectedId =
      state.drawTool.selectedId === zoneId
        ? (newZones.values().next().value?.id ?? null)
        : state.drawTool.selectedId

    return {
      drawTool: {
        ...state.drawTool,
        selectedId: nextSelectedId,
        zoneEditing: {
          ...zoneEditing,
          zones: newZones
        }
      }
    }
  })
}
